import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import "./gridMode.css";
import AudioWaveform from "../Carousel/Carousel";
import kleidoLogo from "../../../assets/images/Kleido.png";

const GridLayout = ({ mediaFiles, postData, onAddComment }) => {
  // ---- Modal / media state ----
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [newComment, setNewComment] = useState("");

  // ---- Like state (toggled by double-click) ----
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(postData?.likes || 0);

  // ---- Comments state (local copy so new comments appear immediately) ----
  const [comments, setComments] = useState(postData?.comments || []);

  // ---- Reply state ----
  const [replyingTo, setReplyingTo] = useState(null); // index of comment being replied to
  const [replyText, setReplyText] = useState("");

  // ---- Share / Suggest state ----
  const [shareCount, setShareCount] = useState(postData?.shares || 0);
  const [suggestCount, setSuggestCount] = useState(postData?.suggestions || 0);
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [suggestUserId, setSuggestUserId] = useState("");
  const [toastMsg, setToastMsg] = useState("");

  // ---- Editable metadata fields ----
  const [mediaLabel, setMediaLabel] = useState(
    postData?.mediaLabel || "Human Curated"
  );
  const [behavioralDepth, setBehavioralDepth] = useState(
    postData?.behavioralDepth || "Very High"
  );

  const qualityRating = postData?.qualityRating || 0;
  const shareDropdownRef = useRef(null);

  // ---- Close share dropdown on outside click ----
  useEffect(() => {
    const handleClick = (e) => {
      if (shareDropdownRef.current && !shareDropdownRef.current.contains(e.target)) {
        setShowShareDropdown(false);
      }
    };
    if (showShareDropdown) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showShareDropdown]);

  // ---- Auto-dismiss toast ----
  useEffect(() => {
    if (toastMsg) {
      const timer = setTimeout(() => setToastMsg(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMsg]);

  // ================================================================
  // MEDIA RENDERER
  // ================================================================
  const renderMedia = (item, isModal = false) => {
    const className = isModal ? "mm-modal-media" : "mm-grid-media";

    switch (item.mediatype) {
      case "image":
        return (
          <img
            src={item.mediaurl}
            alt={item.alt || "Media"}
            className={className}
            onDoubleClick={handleDoubleLike}
          />
        );
      case "video":
        return (
          <video
            src={item.mediaurl}
            className={className}
            controls
            playsInline
            onDoubleClick={handleDoubleLike}
          />
        );
      case "audio":
        return (
          <div className="mm-audio-container">
            <AudioWaveform src={item.mediaurl} />
            <audio src={item.mediaurl} controls />
          </div>
        );
      default:
        return null;
    }
  };

  // ================================================================
  // HANDLERS
  // ================================================================

  /**
   * Add-comment handler — adds to LOCAL state so it appears immediately,
   * AND calls the parent callback for persistence.
   */
  const handleAddComment = () => {
    const trimmed = newComment.trim();
    if (!trimmed) return;

    const newCommentObj = {
      userId: postData?.currentUserId || "You",
      comment: trimmed,
      timestamp: new Date().toISOString(),
      replies: [],
    };

    setComments((prev) => [...prev, newCommentObj]);

    if (onAddComment) {
      onAddComment(postData?.postId, trimmed);
    }

    setNewComment("");
  };

  /**
   * Double-click toggle: if not yet liked → add a like (+1).
   * If already liked → unlike (-1). Reverses the action each time.
   */
  const handleDoubleLike = (e) => {
    e.stopPropagation();
    if (isLiked) {
      setIsLiked(false);
      setLikeCount((prev) => Math.max(0, prev - 1));
    } else {
      setIsLiked(true);
      setLikeCount((prev) => prev + 1);
    }
  };

  // ================================================================
  // REPLY HANDLERS (NEW)
  // ================================================================

  /**
   * Toggle the inline reply input for a specific comment.
   * Clicking Reply on the same comment again closes it.
   */
  const handleReplyClick = (index) => {
    if (replyingTo === index) {
      setReplyingTo(null);
      setReplyText("");
    } else {
      setReplyingTo(index);
      setReplyText("");
    }
  };

  /**
   * Submit a reply — appends it to the parent comment's `replies` array
   * in local state. Also notifies the parent via onAddComment with the
   * parent comment's userId so the backend can thread it.
   */
  const handleReplySubmit = (parentIndex) => {
    const trimmed = replyText.trim();
    if (!trimmed) return;

    const parentComment = comments[parentIndex];
    const newReply = {
      userId: postData?.currentUserId || "You",
      comment: trimmed,
      timestamp: new Date().toISOString(),
      replyTo: parentComment?.userId || null, // who this reply is addressed to
    };

    setComments((prev) =>
      prev.map((c, i) =>
        i === parentIndex
          ? { ...c, replies: [...(c.replies || []), newReply] }
          : c
      )
    );

    // Notify parent for API persistence — pass the parent userId so the
    // backend can thread the reply correctly.
    if (onAddComment) {
      onAddComment(postData?.postId, trimmed, parentComment?.userId);
    }

    setReplyText("");
    setReplyingTo(null);
  };

  // ================================================================
  // SHARE / SUGGEST HANDLERS (unchanged)
  // ================================================================

  const shareUrl = postData?.shareUrl || (typeof window !== "undefined" ? window.location.href : "");
  const shareText = postData?.descriptions || "Check out this content on Kleido!";

  const handleShare = (platform) => {
    setShowShareDropdown(false);
    setShareCount((prev) => prev + 1);

    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);

    switch (platform) {
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
          "_blank",
          "noopener,noreferrer,width=600,height=400"
        );
        break;
      case "instagram":
        copyToClipboard(shareUrl);
        setToastMsg("Link copied! Paste it in Instagram.");
        window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
        break;
      case "snapchat":
        copyToClipboard(shareUrl);
        setToastMsg("Link copied! Paste it in Snapchat.");
        window.open("https://www.snapchat.com/", "_blank", "noopener,noreferrer");
        break;
      case "tiktok":
        copyToClipboard(shareUrl);
        setToastMsg("Link copied! Paste it in TikTok.");
        window.open("https://www.tiktok.com/", "_blank", "noopener,noreferrer");
        break;
      case "native":
        if (navigator.share) {
          navigator.share({ title: "Kleido Content", text: shareText, url: shareUrl })
            .catch(() => {});
        } else {
          copyToClipboard(shareUrl);
          setToastMsg("Link copied to clipboard!");
        }
        break;
      default:
        break;
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(() => {});
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(textarea);
    }
  };

  const handleSuggestSubmit = () => {
    const trimmed = suggestUserId.trim();
    if (!trimmed) {
      setToastMsg("Please enter a valid User ID.");
      return;
    }
    console.log(`[Suggest] Post ${postData?.postId} suggested to user: ${trimmed}`);
    setSuggestCount((prev) => prev + 1);
    setSuggestUserId("");
    setShowSuggestModal(false);
    setToastMsg(`Content suggested to ${trimmed}!`);
  };

  // ================================================================
  // STAR RATING RENDERER
  // ================================================================
  const renderStars = (rating) => {
    return (
      <div className="mm-quality-stars" title={`Quality: ${rating}/5`}>
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill={star <= rating ? "#FFD700" : "none"}
            stroke={star <= rating ? "#FFD700" : "rgba(255,215,0,0.3)"}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        ))}
        <span className="mm-quality-label">{rating}/5</span>
      </div>
    );
  };

  // ================================================================
  // SHARE DROPDOWN RENDERER
  // ================================================================
  const renderShareDropdown = () => (
    <div className="mm-share-dropdown" ref={shareDropdownRef}>
      <button className="mm-share-option" onClick={() => handleShare("facebook")}>
        <span className="mm-share-icon mm-share-icon--facebook">f</span>
        <span>Facebook</span>
      </button>
      <button className="mm-share-option" onClick={() => handleShare("instagram")}>
        <span className="mm-share-icon mm-share-icon--instagram">📷</span>
        <span>Instagram</span>
      </button>
      <button className="mm-share-option" onClick={() => handleShare("snapchat")}>
        <span className="mm-share-icon mm-share-icon--snapchat">👻</span>
        <span>Snapchat</span>
      </button>
      <button className="mm-share-option" onClick={() => handleShare("tiktok")}>
        <span className="mm-share-icon mm-share-icon--tiktok">♪</span>
        <span>TikTok</span>
      </button>
      {typeof navigator !== "undefined" && navigator.share && (
        <button className="mm-share-option" onClick={() => handleShare("native")}>
          <span className="mm-share-icon">📱</span>
          <span>More...</span>
        </button>
      )}
    </div>
  );

  // ================================================================
  // SUGGEST MODAL RENDERER
  // ================================================================
  const renderSuggestModal = () => (
    <div
      className="mm-suggest-overlay"
      onClick={(e) => {
        if (e.target.classList.contains("mm-suggest-overlay")) {
          setShowSuggestModal(false);
        }
      }}
    >
      <div className="mm-suggest-modal">
        <button
          className="mm-suggest-close"
          onClick={() => setShowSuggestModal(false)}
          aria-label="Close"
        >
          ✕
        </button>
        <h3 className="mm-suggest-title">Suggest to User</h3>
        <p className="mm-suggest-help">
          Enter the User ID of the person you want to suggest this content to.
          Anyone with a valid Kleido User ID can receive it.
        </p>
        <div className="mm-suggest-input-row">
          <label className="mm-suggest-label">User ID</label>
          <input
            type="text"
            value={suggestUserId}
            placeholder="e.g. user123"
            onChange={(e) => setSuggestUserId(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSuggestSubmit()}
            className="mm-suggest-input"
            autoFocus
            style={{background: "rgba(36,36,45,0.2)", color: "white"}}
          />
        </div>
        <div className="mm-suggest-actions">
          <button className="mm-suggest-cancel" onClick={() => setShowSuggestModal(false)}>
            Cancel
          </button>
          <button
            className="mm-suggest-submit"
            onClick={handleSuggestSubmit}
            disabled={!suggestUserId.trim()}
            style={{background: "rgba(36,36,45,0.2)", color: "white", border: "1px solid gray"}}
          >
            Send Suggestion
          </button>
        </div>
      </div>
    </div>
  );

  // ================================================================
  // COMMENT RENDERER (NEW — extracted with Reply button + nested replies)
  // ================================================================
  const renderComment = (comment, index) => (
    <div key={index} className="mm-comment-thread">
      {/* Parent comment */}
      <div className="mm-modal-comment">
        <div className="mm-comment-body" style={{gap: 50}}>
          <strong className="mm-modal-comment-user" style={{color: "white"}}>{comment.userId}:</strong>
          <span className="mm-modal-comment-text" style={{marginLeft: 20, color: "white"}}>{comment.comment}</span>
          <span className="mm-modal-comment-time" style={{marginLeft: 20, color: "white"}}>
            {comment.timestamp ? new Date(comment.timestamp).toLocaleString() : ""}
          </span>
        </div>

        {/* Reply button */}
        <button
          className={`mm-reply-btn ${replyingTo === index ? "mm-reply-btn--active" : ""}`}
          onClick={() => handleReplyClick(index)}
          aria-label="Reply to this comment"
        >
          {replyingTo === index ? "Cancel" : "Reply"}
        </button>
      </div>

      {/* Inline reply input (appears when Reply is clicked) */}
      {replyingTo === index && (
        <div className="mm-reply-input-row">
          <span className="mm-reply-mention" style={{color: "white"}}>
            Replying to @{comment.userId}
          </span>
          <div className="mm-reply-input-controls">
            <input
              type="text"
              value={replyText}
              placeholder={`Reply to ${comment.userId}...`}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleReplySubmit(index)}
              className="mm-reply-input"
              autoFocus
              style={{background: "rgba(36,36,45,0.2)", color: "white"}}
            />
            <button
              className="mm-reply-submit-btn"
              onClick={() => handleReplySubmit(index)}
              disabled={!replyText.trim()}
              style={{border: "1px solid darkslategray"}}
            >
              Reply
            </button>
          </div>
        </div>
      )}

      {/* Nested replies (indented under the parent) */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mm-replies-container">
          {comment.replies.map((reply, replyIndex) => (
            <div key={replyIndex} className="mm-reply-item">
              <div className="mm-reply-avatar">
                <span className="mm-reply-avatar-text">
                  {(reply.userId || "?").charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="mm-reply-content">
                <div className="mm-reply-header">
                  <strong className="mm-reply-user" style={{color: "white"}}>{reply.userId}</strong>
                  {reply.replyTo && reply.replyTo !== comment.userId && (
                    <span className="mm-reply-mention-inline">
                      → @{reply.replyTo}
                    </span>
                  )}
                  <span className="mm-reply-time">
                    {reply.timestamp ? new Date(reply.timestamp).toLocaleString() : ""}
                  </span>
                </div>
                <span className="mm-reply-text">{reply.comment}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ================================================================
  // GRID COLUMN CALCULATION
  // ================================================================
  const getGridColumns = () => {
    const count = mediaFiles?.length || 0;
    if (count === 1) return 1;
    if (count === 2) return 2;
    if (count === 3) return 3;
    return 4;
  };

  // ================================================================
  // SINGLE MEDIA EARLY RETURN
  // ================================================================
  if (mediaFiles?.length === 1) {
    return (
      <div className="mm-single-media-container">
        <div
          className="mm-single-media-wrapper"
          onClick={() => setSelectedMedia(mediaFiles[0])}
        >
          {renderMedia(mediaFiles[0])}
        </div>
        {selectedMedia && renderModal()}
      </div>
    );
  }

  // ================================================================
  // MODAL RENDERER
  // ================================================================
  const renderModal = () => (
    <div
      className="mm-modal-overlay"
      onClick={(e) => {
        if (e.target.classList.contains("mm-modal-overlay")) {
          setSelectedMedia(null);
        }
      }}
      style={{ background: "black" }}
    >
      <div className="mm-modal-container" style={{ border: "1px solid darkslategray" }}>
        <button
          className="mm-modal-close"
          onClick={() => setSelectedMedia(null)}
          aria-label="Close"
        >
          ✕
        </button>

        <div className="mm-modal-content">
          {/* ---------- Media Display ---------- */}
          <div className="mm-modal-media-section">
            {renderMedia(selectedMedia, true)}
          </div>

          {/* ---------- Post Details Section ---------- */}
          <div className="mm-modal-details-section">
            {postData?.userImage && (
              <div className="mm-modal-user-info">
                <img
                  src={postData.userImage}
                  alt={postData.postby || "User"}
                  className="mm-modal-user-avatar"
                />
                <div className="mm-modal-user-details">
                  <h3 className="mm-modal-username">
                    {postData.postby || "Unknown"}
                  </h3>
                  <p className="mm-modal-location">
                    📍 {postData.location || "Unknown"} •{" "}
                    {postData.timestamp
                      ? new Date(postData.timestamp).toLocaleString()
                      : "Recently"}
                  </p>
                </div>
              </div>
            )}

            {/* ---------- Logo + Quality Stars ---------- */}
            <div className="mm-logo-quality-row">
              <div className="mm-logo-wrap">
                <img src={kleidoLogo} alt="Kleido" className="mm-modal-logo" />
              </div>
              {renderStars(qualityRating)}
            </div>

            {/* ---------- About Section ---------- */}
            <div className="mm-about-section">
              <h4 className="mm-modal-comments-title">About</h4>

              <div className="mm-about-row">
                <span className="mm-about-label">Posted By:</span>
                {/* <span className="mm-about-value">{postData.postby || "Unknown"}</span> */}
              </div>
              <div className="mm-about-row">
                <span className="mm-about-label">Location:</span>
                {/* <span className="mm-about-value">{postData.location || "Unknown"}</span> */}
              </div>
              <div className="mm-about-row">
                <span className="mm-about-label">Time:</span>
                <span className="mm-about-value">
                  {/* {postData.timestamp
                    ? new Date(postData.timestamp).toLocaleString()
                    : "Recently"} */}
                </span>
              </div>
              {postData?.descriptions && (
                <div className="mm-about-row">
                  <span className="mm-about-label">Description:</span>
                  <span className="mm-about-value">{postData.descriptions}</span>
                </div>
              )}

              <div className="mm-metadata-row">
                <label className="mm-metadata-label">Media Label:</label>
                <p className="mm-metadata-value mm-metadata-highlight">{mediaLabel}</p>
              </div>
              <div className="mm-metadata-row">
                <label className="mm-metadata-label">Behavioral Depth:</label>
                <p className="mm-metadata-value mm-metadata-highlight">{behavioralDepth}</p>
              </div>
            </div>

            {/* ---------- Comments Section (uses renderComment) ---------- */}
            <div className="mm-modal-comments-section">
              <h4 className="mm-modal-comments-title">
                Comments ({comments.length})
              </h4>

              <div className="mm-modal-comments-list" style={{height: 150}}>
                {comments.length > 0 ? (
                  comments.map((comment, index) => renderComment(comment, index))
                ) : (
                  <p className="mm-modal-no-comments">
                    No comments yet. Be the first to comment!
                  </p>
                )}
              </div>

              <div className="mm-modal-add-comment">
                <input
                  type="text"
                  value={newComment}
                  placeholder="Add a comment..."
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleAddComment()}
                  className="mm-modal-comment-input"
                  style={{ color: "white", background: "rgba(36,36,45,0.2)" }}
                />
                <button onClick={handleAddComment} className="mm-modal-comment-button" style={{background: "rgba(36,36,45,0.2)" }}>
                  Post
                </button>
              </div>
            </div>
          </div>

          {/* ---------- Action Bar ---------- */}
          <div className="mm-modal-stats" style={{ background: "rgba(42,42,45,0.2)" }}>
            <div className="mm-modal-stats-left" style={{ display: "flex", flexDirection: "row", gap: 30 }}>
              <div className="mm-modal-stat-item">
                <span className="mm-modal-stat-icon">
                  Like{isLiked ? " ❤️" : ""}
                </span>
                <span className="mm-modal-stat-value">{likeCount}</span>
              </div>

              <div className="mm-modal-stat-item mm-action-btn-wrapper">
                <button
                  className="mm-action-btn"
                  onClick={() => setShowShareDropdown((v) => !v)}
                >
                  <span>🔗 Share</span>
                  <span className="mm-modal-stat-value">{shareCount}</span>
                </button>
                {showShareDropdown && renderShareDropdown()}
              </div>

              <div className="mm-modal-stat-item">
                <button
                  className="mm-action-btn"
                  onClick={() => setShowSuggestModal(true)}
                >
                  <span>💡 Suggest</span>
                  <span className="mm-modal-stat-value">{suggestCount}</span>
                </button>
              </div>
            </div>

            <div className="mm-modal-stats-right">
              <div className="mm-modal-stat-item">
                <span className="mm-modal-stat-icon">Impressions</span>
                <span className="mm-modal-stat-value">
                  {postData?.impressions || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showSuggestModal && renderSuggestModal()}

      {toastMsg && <div className="mm-toast">{toastMsg}</div>}
    </div>
  );

  // ================================================================
  // GRID LAYOUT RENDER
  // ================================================================
  return (
    <div className="mm-grid-layout">
      <div
        className="mm-grid-container"
        style={{ gridTemplateColumns: `repeat(${getGridColumns()}, 1fr)` }}
      >
        {Array.isArray(mediaFiles) &&
          mediaFiles.map((file, index) => (
            <div
              key={index}
              className="mm-grid-item"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMedia(file);
              }}
            >
              {renderMedia(file)}
            </div>
          ))}
      </div>

      {selectedMedia && renderModal()}
    </div>
  );
};

GridLayout.propTypes = {
  mediaFiles: PropTypes.arrayOf(
    PropTypes.shape({
      mediatype: PropTypes.oneOf(["image", "audio", "video"]).isRequired,
      mediaurl: PropTypes.string.isRequired,
      alt: PropTypes.string,
    })
  ).isRequired,
  postData: PropTypes.shape({
    postId: PropTypes.string,
    userImage: PropTypes.string,
    postby: PropTypes.string,
    location: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    descriptions: PropTypes.string,
    likes: PropTypes.number,
    shares: PropTypes.number,
    suggestions: PropTypes.number,
    impressions: PropTypes.number,
    mediaLabel: PropTypes.string,
    behavioralDepth: PropTypes.string,
    qualityRating: PropTypes.number,
    shareUrl: PropTypes.string,
    currentUserId: PropTypes.string,
    comments: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.string,
        comment: PropTypes.string,
        timestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        replies: PropTypes.arrayOf(
          PropTypes.shape({
            userId: PropTypes.string,
            comment: PropTypes.string,
            timestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            replyTo: PropTypes.string,
          })
        ),
      })
    ),
  }),
  onAddComment: PropTypes.func,
};

export default GridLayout;
